import { Link, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { ArrowLeft, ArrowRight } from "untitledui-js/react";
import { paginationLimit } from "../constants";
import { useTranslation } from "react-i18next";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  setPage: (page: number) => void;
  pathname: string;
}

const DEFAULT_CLASSES =
  "flex items-center justify-center h-9 w-9 rounded-full hover:border-secondary-400 focus:border-secondary-400 border border-transparent leading-4 font-bold text-tertiary-700 outline-none dark:text-white/70";
const ARROW_BUTTON_CLASSES = "h-5 w-5 stroke-secondary-700";

const getPaginationNumbers = (
  totalPages: number,
  currentPage: number
): (number | string)[] => {
  if (totalPages <= paginationLimit) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pageNumbers: (number | string)[] = [1];

  // If current page is near the beginning
  if (currentPage <= 3) {
    pageNumbers.push(2, 3, 4, 5);
    if (totalPages > 6) pageNumbers.push("...");
  }
  // If current page is 4, include 3, 4, 5, but stop before 6
  else if (currentPage === 4) {
    pageNumbers.push("...");
    pageNumbers.push(3, 4, 5);
    if (totalPages > 6) pageNumbers.push("...");
  }
  // If current page is near the end
  else if (currentPage >= totalPages - 3) {
    pageNumbers.push("...");
    for (let i = totalPages - 4; i < totalPages; i++) {
      pageNumbers.push(i);
    }
  }
  // If current page is somewhere in the middle
  else {
    pageNumbers.push("...");
    pageNumbers.push(currentPage - 1, currentPage, currentPage + 1);
    if (currentPage + 1 < totalPages - 1) {
      pageNumbers.push("...");
    }
  }

  // Ensure the last page is only added once
  if (pageNumbers[pageNumbers.length - 1] !== totalPages) {
    pageNumbers.push(totalPages);
  }

  return pageNumbers;
};

export const Pagination = ({
  currentPage,
  totalPages,
  setPage,
  pathname,
}: PaginationProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const pageNumbers = getPaginationNumbers(totalPages, currentPage);

  // Preserve existing search parameters except for 'p' (page).
  const newSearchParams = new URLSearchParams(searchParams);
  newSearchParams.delete("p");
  const searchParamsString = newSearchParams.toString();
  const queryParams = searchParamsString ? `&${searchParamsString}` : "";

  return (
    <div className="mt-23.5 flex justify-center gap-x-4.5">
      <Link
        to={`${pathname}?p=${currentPage - 1}${queryParams}`}
        onClick={() => setPage(currentPage - 1)}
        className={twMerge(
          `${DEFAULT_CLASSES} bg-primary-500`,
          currentPage === 1 ? "pointer-events-none bg-tertiary-200" : ""
        )}
        tabIndex={currentPage === 1 ? -1 : 0}
        aria-label={t("Pagination.navigatePreviousPage")}
      >
        <ArrowLeft
          className={twMerge(
            ARROW_BUTTON_CLASSES,
            currentPage === 1 ? "stroke-tertiary-400" : ""
          )}
        />
      </Link>

      <div className="flex justify-center gap-x-3">
        {pageNumbers.map((pageNumber, index) => {
          const showOnMobile =
            currentPage === pageNumber ||
            currentPage - 1 === pageNumber ||
            currentPage + 1 === pageNumber ||
            (currentPage === 1 && currentPage + 2 === pageNumber) ||
            (currentPage === totalPages && currentPage - 2 === pageNumber);

          if (pageNumber === "...") {
            return (
              <span
                key={`${pageNumber}-${index}`}
                className={`${DEFAULT_CLASSES} hidden md:flex`}
              >
                ...
              </span>
            );
          }

          return (
            <Link
              to={`${pathname}?p=${pageNumber}${queryParams}`}
              key={`${pageNumber}-${index}`}
              onClick={() => setPage(pageNumber as number)}
              className={twMerge(
                DEFAULT_CLASSES,
                currentPage === pageNumber
                  ? "bg-tertiary-100 text-tertiary-950 dark:bg-white/20 dark:text-white"
                  : "",
                showOnMobile ? "" : "hidden md:flex"
              )}
            >
              {pageNumber}
            </Link>
          );
        })}
      </div>

      <Link
        to={`${pathname}?p=${currentPage + 1}${queryParams}`}
        onClick={() => setPage(currentPage + 1)}
        className={twMerge(
          `${DEFAULT_CLASSES} bg-primary-500`,
          currentPage === totalPages
            ? "pointer-events-none bg-tertiary-200"
            : ""
        )}
        tabIndex={currentPage === totalPages ? -1 : 0}
        aria-label={t("Pagination.navigateNextPage")}
      >
        <ArrowRight
          className={twMerge(
            ARROW_BUTTON_CLASSES,
            currentPage === totalPages ? "stroke-tertiary-400" : ""
          )}
        />
      </Link>
    </div>
  );
};
